import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ProjectFormDialog({ open, onOpenChange, onSaved, project }) {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name: "",
    client_name: "",
    client_contact: "",
    status: "Active",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name || "",
        client_name: project.client_name || "",
        client_contact: project.client_contact || "",
        status: project.status || "Active",
        description: project.description || "",
      });
    } else {
      setForm({ name: "", client_name: "", client_contact: "", status: "Active", description: "" });
    }
  }, [project, open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = isEdit
        ? await api.patch(`/projects/${project.id}`, form)
        : await api.post(`/projects`, form);
      toast.success(isEdit ? "Project updated" : "Project created");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit Project" : "New Project"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Project Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="project-form-name"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                required
                data-testid="project-form-client"
              />
            </div>
            <div className="space-y-2">
              <Label>Client Contact</Label>
              <Input
                value={form.client_contact}
                onChange={(e) => setForm({ ...form, client_contact: e.target.value })}
                placeholder="phone or email"
                data-testid="project-form-contact"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="project-form-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Paused">Paused</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="project-form-description"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="project-form-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="project-form-save">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
